import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AppState } from './types';
import { ProgressBar } from './components/ProgressBar';
import { Storyboard } from './components/Storyboard';
import { HookScreen } from './components/steps/HookScreen';
import { QuizScreen } from './components/steps/QuizScreen';
import { GroupSetup } from './components/steps/GroupSetup';
import { SpecialtyDeepDive } from './components/steps/SpecialtyDeepDive';
import { TreatmentBehavior } from './components/steps/TreatmentBehavior';
import { DriversBarriers } from './components/steps/DriversBarriers';
import { PatientStaging } from './components/steps/PatientStaging';
import { StrategyMenu } from './components/steps/StrategyMenu';
import { FeatureSelection } from './components/steps/FeatureSelection';
import { MotivationFraming } from './components/steps/MotivationFraming';
import { BuildCard } from './components/steps/BuildCard';
import { PresentationCard } from './components/steps/PresentationCard';
import { AllTogetherView } from './components/steps/AllTogetherView';
import { LayoutGrid } from 'lucide-react';
const TOTAL_STEPS = 13;
const initialState: AppState = {
  quizAnswer: null,
  selectedSpecialty: null,
  groupName: '',
  predictedBehavior: null,
  predictedDriver: null,
  predictedBarrier: null,
  completedChapters: [],
  cardSegmentation: '',
  cardFeatures: '',
  cardMotivation: '',
  cardQuoteForOthers: ''
};
export function App() {
  const [step, setStep] = useState(1);
  const [state, setState] = useState<AppState>(initialState);
  const [direction, setDirection] = useState(1);
  const [showStoryboard, setShowStoryboard] = useState(false);
  const [showStandaloneStoryboard, setShowStandaloneStoryboard] = useState(false);
  const updateState = useCallback((updates: Partial<AppState>) => {
    setState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);
  const nextStep = useCallback(() => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, TOTAL_STEPS));
  }, []);
  const prevStep = useCallback(() => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  }, []);
  const goToStep = useCallback((target: number) => {
    setDirection(target > step ? 1 : -1);
    setStep(Math.max(1, Math.min(target, TOTAL_STEPS)));
  }, [step]);
  const resetApp = useCallback(() => {
    setState(initialState);
    setDirection(-1);
    setStep(1);
  }, []);
  const markChapterAndReturn = useCallback((chapterId: string) => {
    setState(prev => ({
      ...prev,
      completedChapters: prev.completedChapters.includes(chapterId) ? prev.completedChapters : [...prev.completedChapters, chapterId]
    }));
    setDirection(-1);
    setStep(7);
  }, []);
  const stepProps = {
    state,
    updateState,
    nextStep,
    prevStep,
    goToStep,
    resetApp
  };
  // Standalone storyboard mode
  if (showStandaloneStoryboard) {
    return <Storyboard isOpen={true} standalone={true} onClose={() => setShowStandaloneStoryboard(false)} currentStep={step} onGoToStep={target => {
      goToStep(target);
      setShowStandaloneStoryboard(false);
    }} data-id="element-0" />;
  }
  const renderStep = () => {
    switch (step) {
      case 1:
        return <HookScreen {...stepProps} onOpenStoryboard={() => setShowStandaloneStoryboard(true)} data-id="element-1" />;
      case 2:
        return <QuizScreen {...stepProps} data-id="element-2" />;
      case 3:
        return <GroupSetup {...stepProps} data-id="element-3" />;
      case 4:
        return <SpecialtyDeepDive {...stepProps} data-id="element-4" />;
      case 5:
        return <TreatmentBehavior {...stepProps} data-id="element-5" />;
      case 6:
        return <PatientStaging {...stepProps} data-id="element-6" />;
      case 7:
        return <StrategyMenu {...stepProps} data-id="element-7" />;
      case 8:
        return <DriversBarriers {...stepProps} nextStep={() => markChapterAndReturn('segmentation')} data-id="element-8" />;
      case 9:
        return <FeatureSelection {...stepProps} nextStep={() => markChapterAndReturn('arguments')} data-id="element-9" />;
      case 10:
        return <MotivationFraming {...stepProps} nextStep={() => markChapterAndReturn('motivations')} data-id="element-10" />;
      case 11:
        return <BuildCard {...stepProps} data-id="element-11" />;
      case 12:
        return <PresentationCard {...stepProps} data-id="element-12" />;
      case 13:
        return <AllTogetherView {...stepProps} data-id="element-13" />;
      default:
        return <HookScreen {...stepProps} onOpenStoryboard={() => setShowStandaloneStoryboard(true)} data-id="element-14" />;
    }
  };
  return <div className="min-h-screen bg-white text-gray-900 font-sans" data-id="element-15">
      {step > 1 && <ProgressBar currentStep={step} totalSteps={TOTAL_STEPS} onBack={prevStep} onSkip={nextStep} data-id="element-16" />}
      <AnimatePresence mode="wait" data-id="element-17">
        <motion.div key={step} initial={{
        opacity: 0,
        y: direction * 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: direction * -20
      }} transition={{
        duration: 0.35,
        ease: 'easeInOut'
      }} className="h-full flex flex-col" data-id="element-18">
          {renderStep()}
        </motion.div>
      </AnimatePresence>

      {/* Storyboard floating button */}
      <button onClick={() => setShowStoryboard(true)} className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-dark text-white rounded-full shadow-lg hover:bg-black hover:shadow-xl transition-all duration-200 flex items-center justify-center group" aria-label="Open storyboard" data-id="element-19">
        <LayoutGrid size={20} data-id="element-20" />
        <span className="absolute right-full mr-3 bg-dark text-white text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-21">
          Storyboard
        </span>
      </button>

      {/* Storyboard overlay */}
      <Storyboard isOpen={showStoryboard} onClose={() => setShowStoryboard(false)} currentStep={step} onGoToStep={goToStep} data-id="element-22" />
    </div>;
}